import React, { Fragment, useCallback, useEffect, useState } from "react";
import { mergeDeepRight } from "ramda";
import { useCreatorModules } from "../../creatorServices";
import { notBlank } from "../../../util/ext";

import ModuleTable from "./ModuleTable";
import MediaBrowser from "../../common/MediaBrowser";
import TotalsHeader from "../../common/TotalsHeader";
import ModuleFilters from "./ModuleFilters";
import AddNewModuleDialog from "./AddNewModuleDialog";
import RealTimeSearchBar from "../../common/RealTimeSearchBar";

import { useNavigate, useLocation } from "react-router-dom";
import { useSearchQuery } from "../../common/use-searchQuery";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import AddIcon from "@mui/icons-material/Add";

import makeStyles from "@mui/styles/makeStyles";
import ModuleShowcaseDialog from "./showcase/ModuleShowcaseDialog";

const useStyles = makeStyles(theme => ({
  addNewButton: {
    marginRight: "30px",
  },
}));

const getInitialModulesState = (initialState = {}) =>
  mergeDeepRight(
    {
      appliedQuery: "",
      page: 0,
      limit: 10,
      orderBy: "name",
      orderDirection: "asc",
      otherParams: {
        status: "ACTIVE,INACTIVE,DELETED",
      },
    },
    initialState,
  );

const ModuleBrowser = props => {
  const classes = useStyles();
  const navigate = useNavigate();
  const location = useLocation();

  const initialModulesState = getInitialModulesState(
    notBlank(location.state) && notBlank(location.state.search.modules)
      ? location.state.search.modules
      : {},
  );

  const { view } = props;
  const [scrollVisible, setScrollVisible] = useState(true);
  const [modulesSelected, setModulesSelected] = useState([]);

  //-- Handle Search state
  const {
    state,
    handleUpdateSort,
    handleUpdateQuery,
    handleUpdatePage,
    handleUpdateOtherParams,
  } = useSearchQuery(initialModulesState);
  const [queryState, setQueryState] = useState({
    limit: initialModulesState.limit,
    offset: state.limit * state.page,
    sort: `${state.orderBy} ${state.orderDirection}`,
    status: initialModulesState.otherParams.status,
    q: state.appliedQuery,
  });
  useEffect(() => {
    const sort =
      state.orderBy === null
        ? `${initialModulesState.orderBy} ${initialModulesState.orderDirection}`
        : `${state.orderBy} ${state.orderDirection}`;

    setQueryState({
      limit: state.limit,
      offset: state.limit * state.page,
      sort: sort,
      status: state.otherParams.status,
      q: state.appliedQuery,
    });
  }, [initialModulesState.orderBy, initialModulesState.orderDirection, state]);

  const modulesResults = useCreatorModules(queryState);
  const [modules, setModules] = useState({
    data: [],
    hasMore: null,
    count: 0,
  });
  useEffect(() => {
    if (modulesResults.isSuccess) {
      setModules({
        data: modulesResults.data.contents,
        hasMore: modulesResults.data.hasMore,
        count: modulesResults.data.count,
      });
    }
  }, [modulesResults.isSuccess, modulesResults.data]);

  const handleScrollVisible = useCallback(visible => {
    setScrollVisible(visible);
  }, []);

  const handleSelectChange = useCallback(
    selected => {
      console.log(modulesSelected);
      setModulesSelected(selected);
    },
    [modulesSelected],
  );

  const [open, setOpen] = useState(false);
  const [showcaseDialogOpen, setShowcaseDialogOpen] = useState(false);
  const openNewModuleDialog = () => setOpen(true);
  const onClose = () => setOpen(false);
  const closeNewModuleDialog = ({ redirectPath, newModuleId }) => {
    setOpen(false);
    // Redirect to edit module dialog
    navigate(`${redirectPath}/${newModuleId}`);
  };

  const [actionModule, setActionModule] = useState(null);
  const handleShowcaseClick = useCallback(moduleId => {
    setActionModule(moduleId);
    setShowcaseDialogOpen(true);
  }, []);
  const handleShowcaseClose = useCallback(() => {
    setActionModule(null);
    setShowcaseDialogOpen(false);
  }, []);

  return (
    <Fragment>
      <AddNewModuleDialog
        open={open}
        onSave={closeNewModuleDialog}
        onClose={onClose}
      />
      {showcaseDialogOpen && (
        <ModuleShowcaseDialog
          open={showcaseDialogOpen}
          moduleId={actionModule}
          onCloseClicked={handleShowcaseClose}
        />
      )}
      <MediaBrowser
        loaded={modulesResults.isSuccess}
        scrollVisible={scrollVisible}
        searchBar={
          <RealTimeSearchBar
            debounceDelay={250}
            query={state.appliedQuery}
            onQueryChange={handleUpdateQuery}
          />
        }
        totalsHeader={
          <TotalsHeader
            countText="Module"
            count={
              modulesResults.isLoading
                ? "-"
                : modulesResults.isSuccess
                ? modules.count
                : "-"
            }
            selectedActions={
              <Grid container alignItems="center">
                <Button
                  variant="text"
                  startIcon={<AddIcon />}
                  classes={{ root: classes.addNewButton }}
                  onClick={openNewModuleDialog}
                >
                  Create Module
                </Button>
                <ModuleFilters
                  onChangeFilter={handleUpdateOtherParams}
                  // onChangeFilter={setQueryState}
                  handleUpdateSort={handleUpdateSort}
                />
              </Grid>
            }
          />
        }
        mediaTable={
          <ModuleTable
            view={view}
            searchState={state}
            loading={modulesResults.isLoading}
            moduleList={modulesResults.isSuccess ? modules.data : []}
            total={modulesResults.isSuccess ? modules.count : 0}
            page={state.page}
            orderBy={state.orderBy}
            order={state.orderDirection}
            onSortChange={handleUpdateSort}
            onPageChange={handleUpdatePage}
            onSelectChange={handleSelectChange}
            onScrollVisible={handleScrollVisible}
            onShowcaseClick={handleShowcaseClick}
          />
        }
      />
    </Fragment>
  );
};

export default ModuleBrowser;
